import { useEffect, useState, type ReactNode } from 'react';
import { Spinner, ErrorState } from '../components/ui';
import { useAuth } from './useAuth';
import { useRoles } from './roles';
import { AuthErrorScreen } from './AuthErrorScreen';
import { ensureKeycloakLogin } from './keycloak';
import { decidePanelAccess, type PanelRole } from './panel';

interface PanelGuardProps {
  allowed: PanelRole[];
  children: ReactNode;
}

/**
 * Guard raiz cross-domínio (Fase 3): substitui ProtectedRoute/MerchantRoute/
 * StaffRoute. Reaproveita o hardening do ProtectedRoute (retry de init com
 * latch reset). A decisão de acesso é pura (`decidePanelAccess`); o redirect
 * cross-subdomínio é efeito colateral, NUNCA no render.
 *
 * Construído e testado aqui; nenhuma rota o adota nesta fatia (adoção = Fase 3).
 */
export function PanelGuard({ allowed, children }: PanelGuardProps): JSX.Element {
  const isLoggedIn = useAuth((s) => s.isLoggedIn);
  const roles = useRoles();
  const [loginError, setLoginError] = useState<Error | null>(null);
  // Bumping this re-triggers the effect below to retry ensureKeycloakLogin().
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (isLoggedIn) return;
    setLoginError(null);
    ensureKeycloakLogin().catch((error: unknown) => {
      setLoginError(error instanceof Error ? error : new Error('Falha ao iniciar login'));
    });
  }, [isLoggedIn, attempt]);

  const decision = isLoggedIn
    ? decidePanelAccess(allowed, roles, window.location)
    : null;

  const redirectTo = decision?.kind === 'redirect' ? decision.to : null;
  useEffect(() => {
    // Redirect é efeito, nunca no render.
    if (redirectTo) window.location.replace(redirectTo);
  }, [redirectTo]);

  if (!isLoggedIn) {
    if (loginError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-bg p-4">
          <ErrorState
            title="Não foi possível iniciar o login"
            description={loginError.message}
            onRetry={() => setAttempt((n) => n + 1)}
          />
        </div>
      );
    }
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg p-4">
        <Spinner size="lg" label="Redirecionando para o login..." />
      </div>
    );
  }

  if (decision?.kind === 'allow') return <>{children}</>;
  if (decision?.kind === 'redirect') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg p-4">
        <Spinner size="lg" label="Redirecionando..." />
      </div>
    );
  }
  return <AuthErrorScreen />;
}
